import { ShieldQuestion } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  useModerationQueue,
  useReviewContent,
} from "@/features/discussions/discussions";
import { RowListSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

function Empty({ children }: { children: ReactNode }) {
  return (
    <Card className="animate-page-enter">
      <CardContent className="flex items-center gap-3 py-10">
        <ShieldQuestion
          className="text-muted-foreground size-5"
          strokeWidth={1.5}
        />
        <p className="text-muted-foreground text-sm">{children}</p>
      </CardContent>
    </Card>
  );
}

export default function ModerationPage() {
  const { data, isPending, error } = useModerationQueue();
  const review = useReviewContent();

  const Decide = ({ kind, id }: { kind: "threads" | "posts"; id: number }) => (
    <div className="flex gap-2 pt-1">
      <Button
        size="sm"
        disabled={review.isPending}
        onClick={() => review.mutate({ kind, id, decision: "published" })}
      >
        {review.isPending && <Spinner data-slot="icon" />}
        Publish
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={review.isPending}
        onClick={() => review.mutate({ kind, id, decision: "declined" })}
      >
        Decline
      </Button>
    </div>
  );

  const waiting = (data?.threads.length ?? 0) + (data?.posts.length ?? 0);

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl">Moderation</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        {isPending
          ? "Member posts awaiting review."
          : waiting === 0
            ? "Nothing awaiting review."
            : `${waiting} item${waiting === 1 ? "" : "s"} awaiting review. Members' posts stay hidden until published.`}
      </p>

      {error && <p className="text-destructive mt-6 text-sm">{error.message}</p>}
      {review.error && (
        <p className="text-destructive mt-6 text-sm">{review.error.message}</p>
      )}

      <section className="mt-8">
        <h2 className={sectionTitleClass}>
          Discussions {data && `(${data.threads.length})`}
        </h2>
        <div className="mt-3 space-y-3">
          {isPending ? (
            <RowListSkeleton count={2} />
          ) : data?.threads.length === 0 ? (
            <Empty>No discussions awaiting review.</Empty>
          ) : (
            data?.threads.map((thread) => (
              <Card key={thread.id} className="animate-item-enter">
                <CardContent className="space-y-2">
                  <p className="font-medium">{thread.title}</p>
                  <p className="text-muted-foreground text-xs">
                    {thread.author.name} · {timeAgo(thread.created_at)}
                  </p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {thread.body}
                  </p>
                  <Decide kind="threads" id={thread.id} />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      <section className="mt-8">
        <h2 className={sectionTitleClass}>
          Replies {data && `(${data.posts.length})`}
        </h2>
        <div className="mt-3 space-y-3">
          {isPending ? (
            <RowListSkeleton count={2} />
          ) : data?.posts.length === 0 ? (
            <Empty>No replies awaiting review.</Empty>
          ) : (
            data?.posts.map((post) => (
              <Card key={post.id} className="animate-item-enter">
                <CardContent className="space-y-2">
                  <p className="text-muted-foreground text-xs">
                    {post.author.name} replying to{" "}
                    <Link
                      to={`/discussions/${post.thread_id}`}
                      className="underline-offset-4 hover:underline"
                    >
                      {post.thread_title}
                    </Link>{" "}
                    · {timeAgo(post.created_at)}
                  </p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {post.body}
                  </p>
                  <Decide kind="posts" id={post.id} />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
